import { ColumnDef } from "@tanstack/react-table";

import { getGradientColor } from "./color_range";

const lowHex = "#FFF59D";
const highHex = "#EF9A9A";

export type BiddingEfficiencyRow = {
  BidPriceBucket: string;
  AvgBidPriceCpmMicroEur: number;
  AvgImpValueMicroEur: number;
  AvgMaxBidMicroEur: number;
  AvgSpendAdjustment: number;
  AvgCorrectionCoefficient: number;
  AvgEstimatedWinRate: number;
  AvgWinPriceCpmMicroEur: number;
  WinRate: number;
  Bids: number;
  Impressions: number;
  ImpPerBids: number;
  SupplyCostEur: number;
  RevenueEur: number;
  ProfitEur: number;
  EstimatedInfraCostEur: number;
  GrossProfitEur: number;
  Conversions: number;
  ApConversions: number;
  AvgEstimatedIpm: number;
  ApIpm: number;
  ApCpiEur: number;
  PredictedBidCpiEur: number;
  PredictedImpCpiEur: number;
};

export const columnsA: ColumnDef<BiddingEfficiencyRow>[] = [
  {
    accessorKey: "BidPriceBucket",
    header: "Bid Price Bucket",
  },
  {
    accessorKey: "Bids",
    header: "Bids",
    cell: countCell,
  },
  {
    accessorKey: "Impressions",
    header: "Impressions",
    cell: countCell,
  },
  {
    accessorKey: "AvgBidPriceCpmMicroEur",
    header: "Avg. Bid Price CPM",
    cell: microEuroCell,
  },
  {
    accessorKey: "AvgWinPriceCpmMicroEur",
    header: "Avg. Win Price CPM",
    cell: microEuroCell,
  },
  {
    accessorKey: "AvgEstimatedWinRate",
    header: "Avg. Estimated Win Rate",
    cell: percentCell,
  },
  {
    accessorKey: "WinRate",
    header: "Win Rate",
    cell: percentCell,
  },
  {
    accessorKey: "ImpPerBids",
    header: "Impressions Per Bids",
    cell: percentCell,
  },
];

export const columnsB: ColumnDef<BiddingEfficiencyRow>[] = [
  {
    accessorKey: "BidPriceBucket",
    header: "Bid Price Bucket",
  },
  {
    accessorKey: "AvgImpValueMicroEur",
    header: "Avg. Imp Value",
    cell: microEuroCell,
  },
  {
    accessorKey: "AvgMaxBidMicroEur",
    header: "Avg. Max Bid",
    cell: microEuroCell,
  },
  {
    accessorKey: "AvgBidPriceCpmMicroEur",
    header: "Avg. Bid Price CPM",
    cell: microEuroCell,
  },
  {
    accessorKey: "AvgWinPriceCpmMicroEur",
    header: "Avg. Win Price CPM",
    cell: microEuroCell,
  },
  {
    accessorKey: "AvgSpendAdjustment",
    header: "Avg. Spend Adjustment",
    cell: percentCell,
  },
  {
    accessorKey: "AvgCorrectionCoefficient",
    header: "Avg. Correction Coefficient",
    cell: percentCell,
  },
];

export const columnsC: ColumnDef<BiddingEfficiencyRow>[] = [
  {
    accessorKey: "BidPriceBucket",
    header: "Bid Price Bucket",
  },
  {
    accessorKey: "Conversions",
    header: "Conversions",
    cell: countCell,
  },
  {
    accessorKey: "ApConversions",
    header: "AP Conversions",
    cell: countCell,
  },
  {
    accessorKey: "AvgEstimatedIpm",
    header: "Avg. Estimated IPM",
    cell: percentCell,
  },
  {
    accessorKey: "ApIpm",
    header: "AP IPM",
    cell: percentCell,
  },
  {
    accessorKey: "ApCpiEur",
    header: "AP CPI",
    cell: euroCell,
  },
  {
    accessorKey: "PredictedBidCpiEur",
    header: "Predicted Bid CPI",
    cell: euroCell,
  },
  {
    accessorKey: "PredictedImpCpiEur",
    header: "Predicted Imp CPI",
    cell: euroCell,
  },
];

export const columnsD: ColumnDef<BiddingEfficiencyRow>[] = [
  {
    accessorKey: "BidPriceBucket",
    header: "Bid Price Bucket",
  },
  {
    accessorKey: "SupplyCostEur",
    header: "Supply Cost",
    cell: euroCell,
  },
  {
    accessorKey: "RevenueEur",
    header: "Revenue",
    cell: euroCell,
  },
  {
    accessorKey: "ProfitEur",
    header: "Profit",
    cell: euroCell,
  },
  {
    accessorKey: "EstimatedInfraCostEur",
    header: "Estimated Infra Cost",
    cell: euroCell,
  },
  {
    accessorKey: "GrossProfitEur",
    header: "Gross Profit",
    cell: euroCell,
  },
];

const euroFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  maximumSignificantDigits: 4,

  notation: "compact",
  compactDisplay: "short",
});

function microEuroCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = euroFormatter.format(value / 1e6);
  return (
    <div className="p-1 text-right font-medium text-slate-900" style={{ backgroundColor: col }}>
      {formatted}
    </div>
  );
}

function euroCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = euroFormatter.format(value);
  return (
    <div className="p-1 text-right font-medium text-slate-900" style={{ backgroundColor: col }}>
      {formatted}
    </div>
  );
}

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumSignificantDigits: 4,
  notation: "compact",
  compactDisplay: "short",
});

function percentCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = percentFormatter.format(value);
  return (
    <div className="p-1 text-right font-medium text-slate-900" style={{ backgroundColor: col }}>
      {formatted}
    </div>
  );
}

const countFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  notation: "compact",
  compactDisplay: "short",
});

function countCell(x: any): JSX.Element {
  const range = x.row.original[x.column.id + "_range"] as number;
  const col = getGradientColor(range, lowHex, highHex);

  const value = x.getValue() as number;
  const formatted = countFormatter.format(value);
  return (
    <div className="p-1 text-right font-medium text-slate-900" style={{ backgroundColor: col }}>
      {formatted}
    </div>
  );
}
